/**
 * Creative Studio · Step 1 — Single input + optional product/reference uploads.
 *
 * The ONLY required field is the prompt. Uploads are progressive disclosure
 * and stay collapsed unless the founder explicitly opens "Add product images
 * or references". This keeps the page feeling like one decision, not a form.
 */
import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, ArrowRight, ArrowsClockwise, Briefcase, Plus, X, Image as ImgIcon, CaretDown,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const LIME = '#c8f060';

const EXAMPLE_PROMPTS = [
  'New spring collection drop',
  'Restaurant brunch launch',
  'SaaS founder announcement',
  'Lead-magnet for designers',
  'Beauty product launch — serum',
  'Free webinar for consultants',
];

const MAX_UPLOADS = 4;
const MAX_BYTES = 5 * 1024 * 1024;  // 5 MB / file

export default function InputStep({ value, setValue, onRun, loading, inheritedStrategy, uploads, setUploads }) {
  const fileInputRef = useRef(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    const remaining = MAX_UPLOADS - uploads.length;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_UPLOADS} images.`);
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    for (const file of list) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: too large (max 5 MB).`);
        continue;
      }
      const tempId = Math.random().toString(36).slice(2, 10);
      const localPreview = URL.createObjectURL(file);
      setUploads((prev) => [...prev, { id: tempId, name: file.name, url: localPreview, uploading: true }]);
      setUploadingIdx(tempId);
      try {
        const form = new FormData();
        form.append('file', file);
        const { data } = await axios.post(`${API}/api/creative-studio/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploads((prev) => prev.map((u) =>
          u.id === tempId
            ? { id: tempId, name: file.name, url: data.url, asset_id: data.asset_id, uploading: false }
            : u,
        ));
      } catch (e) {
        toast.error(`${file.name}: upload failed.`);
        setUploads((prev) => prev.filter((u) => u.id !== tempId));
      }
    }
    setUploadingIdx(null);
  };

  const removeUpload = (id) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -12 }}
      className="max-w-2xl mx-auto" data-testid="creative-input-step">
      <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: LIME }}>Creative Studio</p>
      <h1 className="text-4xl sm:text-5xl mb-3" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 500, color: '#f5f0e8' }}>
        What are you promoting?
      </h1>
      <p className="text-base mb-7" style={{ color: 'rgba(245,240,232,0.55)', lineHeight: 1.6 }}>
        One sentence. We&rsquo;ll write the brief, pick the creative direction, and ship the whole campaign: flyer, carousel, social posts, email, ads.
      </p>

      {inheritedStrategy && (
        <div className="mb-5 px-4 py-3 rounded-2xl flex items-center gap-3"
          style={{ background: 'rgba(200,240,96,0.05)', border: '1px solid rgba(200,240,96,0.18)' }}
          data-testid="creative-strategy-banner">
          <Briefcase size={18} weight="fill" style={{ color: LIME, flexShrink: 0 }} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: LIME }}>Inheriting from your active campaign</p>
            <p className="text-sm font-semibold truncate" style={{ color: '#fff' }}>{inheritedStrategy.campaign_name}</p>
          </div>
        </div>
      )}

      <div className="rounded-3xl p-1.5" style={{ background: 'linear-gradient(135deg, rgba(200,240,96,0.18), rgba(255,255,255,0.04))' }}>
        <div className="rounded-[22px] p-5" style={{ background: '#0c0c0c' }}>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. summer linen collection launch"
            rows={3}
            data-testid="creative-prompt-input"
            className="w-full bg-transparent outline-none resize-none text-lg"
            style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif", lineHeight: 1.5 }}
          />
          <div className="flex flex-wrap gap-2 mt-3">
            {EXAMPLE_PROMPTS.slice(0, 4).map((p) => (
              <button key={p} onClick={() => setValue(p)} type="button"
                className="px-2.5 py-1 rounded-full text-[11px] transition hover:opacity-100"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.55)', border: '1px solid rgba(255,255,255,0.07)', opacity: 0.85 }}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Advanced: image uploads */}
      <button type="button" onClick={() => setAdvancedOpen(!advancedOpen)}
        className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold"
        data-testid="creative-advanced-toggle"
        style={{ color: 'rgba(245,240,232,0.5)' }}>
        <CaretDown size={11} style={{ transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        {uploads.length ? `${uploads.length} reference${uploads.length === 1 ? '' : 's'} attached` : 'Add product images or references'}
      </button>

      {advancedOpen && (
        <div className="mt-3 p-4 rounded-2xl" style={{ background: '#0c0c0c', border: '1px dashed rgba(255,255,255,0.08)' }}>
          <p className="text-[11px] mb-3" style={{ color: 'rgba(245,240,232,0.5)' }}>
            Drop product photos, logos, or campaign refs (max {MAX_UPLOADS} · 5 MB each). The strategist will pull palette, mood, and product context from these.
          </p>
          <div className="flex flex-wrap gap-3">
            {uploads.map((u) => (
              <div key={u.id} className="relative group w-20 h-20 rounded-xl overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                data-testid={`upload-tile-${u.id}`}>
                <img src={u.url} alt={u.name} className="w-full h-full object-cover" />
                {u.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}>
                    <ArrowsClockwise size={14} className="animate-spin" style={{ color: '#fff' }} />
                  </div>
                )}
                <button type="button" onClick={() => removeUpload(u.id)} data-testid={`upload-remove-${u.id}`}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                  style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}>
                  <X size={10} weight="bold" />
                </button>
              </div>
            ))}
            {uploads.length < MAX_UPLOADS && (
              <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="upload-add-btn"
                className="w-20 h-20 rounded-xl flex flex-col items-center justify-center gap-1 transition hover:opacity-90"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.18)', color: 'rgba(245,240,232,0.6)' }}>
                <Plus size={16} />
                <span className="text-[9px] tracking-[0.18em] uppercase">add</span>
              </button>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/png,image/jpeg,image/webp"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="upload-file-input"
          />
        </div>
      )}

      <div className="flex items-center justify-between mt-6">
        <p className="text-[12px]" style={{ color: 'rgba(245,240,232,0.4)' }}>No design choices required. The strategist makes them.</p>
        <button onClick={onRun} disabled={loading || !value.trim() || !!uploadingIdx}
          data-testid="creative-brief-btn"
          className="px-7 py-3 rounded-full font-bold text-sm flex items-center gap-2 transition"
          style={{
            background: value.trim() && !loading ? LIME : 'rgba(255,255,255,0.08)',
            color: value.trim() && !loading ? '#0a0a0a' : 'rgba(245,240,232,0.4)',
            cursor: value.trim() && !loading ? 'pointer' : 'not-allowed',
            boxShadow: value.trim() && !loading ? '0 8px 30px -10px rgba(200,240,96,0.5)' : 'none',
          }}>
          {loading
            ? (<><ArrowsClockwise size={16} className="animate-spin" /> Reading your brand…</>)
            : (<>Direct the creative <ArrowRight size={14} /></>)}
        </button>
      </div>
    </motion.div>
  );
}
